import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  useTheme,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  alpha,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { DoubleMaterialityActivationModal, DoubleMaterialitySurveyActivationStrategy } from "./DoubleMaterialityActivationModal";

import { useGetStakeholders } from "@api";
import {
  QuestionAnswerRow,
  useGetQuestionAnswers,
} from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useGetSurveys, useUpdateSurvey } from "@api/hooks/doubleMateriality";
import { useUserStore } from "@store/user";
import { getYearFromSearchParams } from "@helpers";

const CampaignStatusCard: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();

  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);

  const { stakeholders } = useGetStakeholders(user, {
    pageNumber: 1,
    pageSize: 1000,
    year,
  });
  const { answers } = useGetQuestionAnswers(user);
  const { surveys } = useGetSurveys(user);
  const { updateSurveyAsync, isPendingUpdateSurvey } = useUpdateSurvey(user);

  const [showActivationModal, setShowActivationModal] = useState(false);

  const currentSurvey = surveys?.find((s) => s.year === year);
  const campaignActive = currentSurvey?.isActive || false;

  const stakeholdersList = stakeholders?.data ?? [];
  const answersList = (answers ?? []) as QuestionAnswerRow[];

  const stakeholderStats = new Map<string, { total: number; answered: number }>();

  answersList.forEach((answer) => {
    const stakeholderId = answer.stakeholderId;

    if (!stakeholderId) return;

    const stats = stakeholderStats.get(stakeholderId) ?? { total: 0, answered: 0 };

    stats.total += 1;
    if (answer.answeredAt) stats.answered += 1;
    stakeholderStats.set(stakeholderId, stats);
  });

  const stakeholdersWithAssignedQuestions = stakeholdersList.filter((stakeholder) =>
    stakeholderStats.has(stakeholder.id),
  );

  const handleCampaignToggle = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const isActive = event.target.checked;

    if (!currentSurvey) {
      enqueueSnackbar(
        t("doubleMateriality.surveyNotFound", "Survey not found for this year"),
        { variant: "error" },
      );

      return;
    }

    if (isActive) {
      setShowActivationModal(true);

      return;
    }

    // Deactivation path
    try {
      await updateSurveyAsync({
        surveyId: currentSurvey.id,
        params: { isActive: false },
      });
      enqueueSnackbar(
        t(
          "doubleMateriality.campaignDeactivated",
          "Campaign deactivated successfully",
        ),
        { variant: "success" },
      );
    } catch {
      enqueueSnackbar(
        t(
          "doubleMateriality.campaignToggleError",
          "Failed to toggle campaign status",
        ),
        { variant: "error" },
      );
    }
  };

  const handleActivationConfirm = async (strategy: DoubleMaterialitySurveyActivationStrategy): Promise<void> => {
    if (!currentSurvey) return;

    try {
      await updateSurveyAsync({
        surveyId: currentSurvey.id,
        params: {
          isActive: true,
          activationEmailStrategy: strategy,
        },
      });
      enqueueSnackbar(
        t(
          "doubleMateriality.campaignActivated",
          "Campaign activated successfully. Emails are being sent to stakeholders.",
        ),
        { variant: "success" },
      );
    } catch {
      enqueueSnackbar(
        t(
          "doubleMateriality.campaignToggleError",
          "Failed to toggle campaign status",
        ),
        { variant: "error" },
      );
    }
  };

  return (
    <Card
      sx={{
        bgcolor: campaignActive
          ? theme.palette.custom.teal[50]
          : alpha(theme.palette.error.main, 0.08),
      }}
    >
      <CardContent sx={{ py: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 3, md: 4 }}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box sx={{ minWidth: 220, paddingLeft: 2 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t("doubleMateriality.campaignStatus", "Campaign status")}
            </Typography>
            <Box sx={{ ml: 1.4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={campaignActive}
                    onChange={handleCampaignToggle}
                    color="primary"
                    disabled={isPendingUpdateSurvey || !currentSurvey}
                  />
                }
                label={
                  <Chip
                    label={campaignActive ? "Active" : "Inactive"}
                    color={campaignActive ? "success" : "error"}
                    variant="filled"
                    size="small"
                  />
                }
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              alignSelf: "stretch",
              width: "1px",
              bgcolor: alpha(theme.palette.custom.teal[500], 0.6),
              mx: 4,
              borderRadius: 1,
            }}
          />

          <Box flex={1} sx={{ pl: { md: 2 } }}>
            <Typography variant="body2" color="text.secondary">
              {campaignActive
                ? t(
                  "doubleMateriality.campaignActiveDescription",
                  "Campaign is active. Sent out {{count}} notifications.",
                  { count: stakeholdersWithAssignedQuestions.length },
                )
                : t(
                  "doubleMateriality.campaignInactive",
                  "Enable the notification campaign to send alerts to each stakeholder. The first notification will be sent immediately after the campaign is activated. If a user doesn't complete the questionnaire, a reminder will be sent 1 day before the deadline and daily after the deadline.",
                )}
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      {currentSurvey && (
        <DoubleMaterialityActivationModal
          open={showActivationModal}
          onClose={() => setShowActivationModal(false)}
          onConfirm={handleActivationConfirm}
          surveyId={currentSurvey.id}
        />
      )}
    </Card>
  );
};

export { CampaignStatusCard };
